import React, { useRef, useState } from 'react';
import useBanquetEntry from '../../hooks/useBanquetEntry';
import { submitBanquet } from '../../utils/banquetApi';
import SalesService from '../../services/salesService';
import BanquetEntryFields from './BanquetEntryFields';
import BanquetItemRows from './BanquetItemRows';
import BanquetShortageModal, { normaliseShortages } from './BanquetShortageModal';
import './banquet.css';

/**
 * ============================================================================
 * BANQUET ENTRY — the whole lane, in one mountable block
 * ============================================================================
 *
 * WHY THE STATE LIVES HERE AND NOT IN TodaysSalesReport
 * -----------------------------------------------------
 * Leaving banquet mode UNMOUNTS this component, and unmounting is what clears
 * the banquet state — the fields, the rows, the cascade flags, every one of
 * them, with no reset code to forget to update when a field is added later.
 * Hoisting this state into the TSR page would have meant a manual teardown that
 * silently rots the first time somebody adds a field and does not extend it.
 *
 * STOCK, EXACTLY AS A TSR
 * -----------------------
 * A banquet deducts stock through the same engine as a TSR line, so it asks
 * the same question first: /sales/pre-check-stock, the endpoint the TSR page
 * uses. If anything is short the owner gets the TSR's choice — create negative
 * stock, or stop — and only an explicit "Confirm & Create Negative Stock"
 * sends allowNegativeStock. The pre-check is advisory: the server checks again
 * inside the transaction, and a shortage it finds (a race with another write)
 * opens the same popup.
 *
 * WHAT THIS COMPONENT STILL DOES NOT DO
 * ------------------------------------
 * It never touches scanItems, manualItems, totalAmount or the NC flow, and it
 * never posts to /tsr/submit: a banquet row reaching the regular submit would
 * write a zero-priced line into the sales ledger.
 */

const dmy = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : String(iso || '');
};

/** The words for a refusal that has no field to sit beside. */
export function refusalMessage(res) {
  if (res.code === 'PERIOD_RECONCILED' && res.reconciledThrough) {
    return `Stock up to ${dmy(res.reconciledThrough)} is already counted. Banquets can only be recorded after that date.`;
  }
  if (res.code === 'UNIT_FAMILY_MISMATCH' && Array.isArray(res.mismatches) && res.mismatches.length > 0) {
    const rows = res.mismatches.map((m) => `${m.menuItemName} / ${m.ingredientName}`).join(', ');
    return `Some recipes use a unit that does not match the ingredient's stock unit: ${rows}. Fix them in Item Mapping before recording this banquet.`;
  }
  return res.error || 'Could not record this banquet.';
}

const BanquetSection = ({
  eventDate,          // the page's selected report date, read-only here
  disabled,           // the page is busy
  getSuggestions,     // the page's own menu matcher
  getMenuItemName,    // the page's own name accessor
  onRecorded,         // (event) => void — the page shows its own success banner
}) => {
  const banquet = useBanquetEntry();
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [fieldError, setFieldError] = useState(null);   // { field, message }
  const [formError, setFormError] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [shortages, setShortages] = useState(null);     // rows awaiting "Confirm & Create Negative Stock"

  const busy = disabled || submitting || checking;

  const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

  const record = async (allowNegativeStock) => {
    setSubmitting(true);
    setFieldError(null);
    setFormError(null);

    const res = await submitBanquet({
      eventDate,
      functionName: banquet.state.functionName.trim(),
      platesOrdered: banquet.state.platesOrdered,
      ratePerPlate: Number(banquet.state.ratePerPlate),
      items: banquet.payloadItems,
      notInMenuItems: banquet.payloadNotInMenu,
      allowNegativeStock,
    });

    setSubmitting(false);

    if (res.ok) {
      setShortages(null);
      if (onRecorded) onRecorded(res);
      banquet.reset();
      return;
    }

    if (res.code === 'STOCK_SHORTAGE') {
      setShortages(normaliseShortages(res.shortages));
      return;
    }
    setShortages(null);

    if (res.code === 'BANQUET_DUPLICATE') {
      // No "continue anyway". A banquet has no legitimate identical twin: the
      // same function, on the same date, for the same plates at the same rate
      // with the same dishes is the same event. The way out is Revert Banquet.
      setDuplicate(res.existing || {});
      return;
    }

    // A coded field error points at the box that caused it; anything else is
    // shown whole, because a message with nowhere to sit is still information.
    if (res.field) setFieldError({ field: res.field, message: res.error });
    else setFormError(refusalMessage(res));
  };

  const handleSubmit = async () => {
    if (!banquet.canSubmit || busy) return;
    setFieldError(null);
    setFormError(null);

    if (banquet.payloadItems.length > 0) {
      setChecking(true);
      let check = null;
      try {
        check = await SalesService.preCheckStock({ items: banquet.payloadItems });
      } catch (e) {
        check = null;   // advisory only — the server checks again inside the transaction
      }
      setChecking(false);
      if (check && check.success && check.hasNegativeStock) {
        setShortages(normaliseShortages(check.missingIngredients));
        return;
      }
    }

    await record(false);
  };

  const [detailsConfirmed, setDetailsConfirmed] = useState(false);
  const itemsSectionRef = useRef(null);

  const hasEnteredDetails = Boolean(
    String(banquet.state.functionName || '').trim().length > 0 &&
    banquet.state.platesOrdered !== null &&
    banquet.state.platesOrdered > 0 &&
    banquet.state.ratePerPlate !== null &&
    Number(banquet.state.ratePerPlate) >= 0
  );

  const handleConfirmDetails = () => {
    if (hasEnteredDetails) {
      setDetailsConfirmed(true);
      if (banquet.state.rows.length === 0 && banquet.addEmptyRow) {
        banquet.addEmptyRow();
      }
      setTimeout(() => {
        const el = itemsSectionRef.current || document.getElementById('bq-items-section') || document.querySelector('.tsr-manual-entry-wrap');
        if (el) {
          const container = el.closest('.content-wrapper') || document.querySelector('.content-wrapper');
          if (container && typeof container.scrollTo === 'function' && typeof container.getBoundingClientRect === 'function') {
            const containerRect = container.getBoundingClientRect();
            const elRect = el.getBoundingClientRect();
            const targetTop = container.scrollTop + (elRect.top - containerRect.top);
            container.scrollTo({ top: targetTop, behavior: 'smooth' });
          } else if (typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 120);
    }
  };

  return (
    <div className="bq-section">
      <BanquetEntryFields
        functionName={banquet.state.functionName}
        platesOrdered={banquet.state.platesOrdered}
        ratePerPlate={banquet.state.ratePerPlate}
        totalBill={banquet.totalBill}
        lastCascade={banquet.state.lastCascade}
        disabled={busy}
        fieldError={fieldError}
        canConfirm={hasEnteredDetails}
        isConfirmed={detailsConfirmed}
        onConfirm={handleConfirmDetails}
        onFieldChange={(field, val) => {
          setDetailsConfirmed(false);
          banquet.setField(field, val);
        }}
        onPlatesChange={(n) => {
          setDetailsConfirmed(false);
          banquet.setPlates(n);
        }}
      />

      {hasEnteredDetails && detailsConfirmed && (
        <div ref={itemsSectionRef} id="bq-items-section" style={{ width: '100%' }}>
          <BanquetItemRows
            rows={banquet.state.rows}
            platesOrdered={banquet.state.platesOrdered}
            canAddItems={banquet.canAddItems}
            disabled={busy}
            error={banquet.state.error}
            getSuggestions={getSuggestions}
            getMenuItemName={getMenuItemName}
            onAddItem={banquet.addItem}
            onAddEmptyRow={banquet.addEmptyRow}
            onUpdateRowItem={banquet.updateRowItem}
            onSetRowQty={banquet.setRowQty}
            onResetRowQty={banquet.resetRowQty}
            onRemoveRow={banquet.removeRow}
            onClearError={banquet.clearError}
            onMarkNotInMenu={banquet.markNotInMenu}
          />

          {banquet.totalBill !== null && (
            <div className="tsr-total-card">
              <span className="tsr-total-label tsr-total-label--stacked">
                Total Bill
                <span className="tsr-total-count">
                  {banquet.state.platesOrdered} plates
                  {banquet.overrideCount > 0 && ` · ${banquet.overrideCount} override${banquet.overrideCount === 1 ? '' : 's'}`}
                </span>
              </span>
              <span className="tsr-total-amount">{money(banquet.totalBill)}</span>
            </div>
          )}

          {formError && <div className="bq-form-error">{formError}</div>}

          <div className="button-container">
            <button
              type="button"
              className={`confirm-button ${submitting || checking ? 'loading' : ''}`}
              style={{ height: '52px', minHeight: '52px', maxHeight: '52px', boxSizing: 'border-box', padding: '0 15px' }}
              onClick={handleSubmit}
              disabled={!banquet.canSubmit || busy}
            >
              {submitting ? 'Recording…' : checking ? 'Checking stock…' : 'Record Banquet'}
            </button>
          </div>
        </div>
      )}

      {shortages && (
        <BanquetShortageModal
          shortages={shortages}
          busy={submitting}
          onConfirm={() => record(true)}
          onCancel={() => setShortages(null)}
        />
      )}

      {duplicate && (
        <div className="bq-modal-overlay" onClick={() => setDuplicate(null)}>
          <div className="bq-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="bq-modal-title">This banquet is already recorded</h3>
            <p className="bq-modal-body">
              {duplicate.eventId ? (
                <>
                  <strong>{duplicate.functionName}</strong> on {dmy(duplicate.eventDate)}
                  {duplicate.platesOrdered ? ` · ${duplicate.platesOrdered} plates` : ''}
                  {duplicate.totalBill != null ? ` · ${money(duplicate.totalBill)}` : ''}
                  {' '}is already on record.
                </>
              ) : (
                <>An identical banquet was recorded a moment ago.</>
              )}
            </p>
            <p className="bq-modal-note">
              Nothing was recorded just now. To change it, use <strong>Revert Banquet</strong> from
              the menu and enter it again.
            </p>
            <button type="button" className="bq-modal-btn" onClick={() => setDuplicate(null)}>
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BanquetSection;
